// HolidayFormModal.jsx
//
// Create or edit a holiday. The form is rule-type-aware: when the user
// picks a rule type, the appropriate sub-fields appear:
//   - fixed_date    → month + day
//   - floating_dow  → month + week + day-of-week
//   - easter_offset → offset days
//   - custom        → raw JSON textarea (advanced)
//
// On save we build the rule_config JSON from the sub-fields. For edit
// mode, the initial data is passed in via `initialData` (the row from
// the holiday list - no separate fetch needed).

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Select, TextField,
  Typography, InputLabel, FormControl,
} from "@mui/material";

import { createHoliday, updateHoliday } from "../../../services/forecast/forecastservices";

const BLUE = "#378ADD";

const HOLIDAY_TYPES = ["federal", "state", "behavioral"];
const RULE_TYPES = [
  { value: "fixed_date",    label: "Fixed date (e.g. Jan 1)" },
  { value: "floating_dow",  label: "Floating day-of-week (e.g. 4th Thu of Nov)" },
  { value: "easter_offset", label: "Easter offset (e.g. Easter -2 days)" },
  { value: "custom",        label: "Custom (raw JSON)" },
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DOWS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const WEEKS  = [
  { value: 1,  label: "1st" }, { value: 2,  label: "2nd" },
  { value: 3,  label: "3rd" }, { value: 4,  label: "4th" },
  { value: 5,  label: "5th" }, { value: -1, label: "Last" },
];


export default function HolidayFormModal({ open, mode, initialData, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [name,         setName]         = useState("");
  const [holidayType,  setHolidayType]  = useState("federal");
  const [ruleType,     setRuleType]     = useState("fixed_date");
  const [displayOrder, setDisplayOrder] = useState(100);
  const [isActive,     setIsActive]     = useState(true);

  // Rule-config sub-fields
  const [month,        setMonth]        = useState(1);
  const [day,          setDay]          = useState(1);
  const [week,         setWeek]         = useState(1);
  const [dow,          setDow]          = useState(0);
  const [offsetDays,   setOffsetDays]   = useState(0);
  const [customJson,   setCustomJson]   = useState("");

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  // ── Load on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);

    if (isEdit && initialData) {
      setName(initialData.name || "");
      setHolidayType(initialData.holiday_type || "federal");
      setRuleType(initialData.rule_type || "fixed_date");
      setDisplayOrder(initialData.display_order ?? 100);
      setIsActive(initialData.is_active !== false);

      // Parse existing rule_config JSON into sub-fields
      let cfg = {};
      try {
        cfg = typeof initialData.rule_config === "string"
          ? JSON.parse(initialData.rule_config)
          : (initialData.rule_config || {});
      } catch { cfg = {}; }

      setMonth(cfg.month ?? 1);
      setDay(cfg.day ?? 1);
      setWeek(cfg.week ?? 1);
      setDow(cfg.dow ?? 0);
      setOffsetDays(cfg.offset_days ?? 0);
      setCustomJson(JSON.stringify(cfg, null, 2));
    } else {
      // Create mode defaults
      setName("");
      setHolidayType("federal");
      setRuleType("fixed_date");
      setDisplayOrder(100);
      setIsActive(true);
      setMonth(1); setDay(1); setWeek(1); setDow(0); setOffsetDays(0);
      setCustomJson("{}");
    }
  }, [open, isEdit, initialData]);

  // ── Compute rule_config from sub-fields ───────────────────────
  const ruleConfig = useMemo(() => {
    switch (ruleType) {
      case "fixed_date":    return { month: Number(month), day: Number(day) };
      case "floating_dow":  return { month: Number(month), week: Number(week), dow: Number(dow) };
      case "easter_offset": return { offset_days: Number(offsetDays) };
      case "custom":
        try   { return JSON.parse(customJson); }
        catch { return null; }
      default: return {};
    }
  }, [ruleType, month, day, week, dow, offsetDays, customJson]);

  // ── Validate ──────────────────────────────────────────────────
  const validate = () => {
    if (!name.trim())                              return "Name is required";
    if (!HOLIDAY_TYPES.includes(holidayType))      return "Invalid holiday type";
    if (!RULE_TYPES.some(r => r.value === ruleType)) return "Invalid rule type";

    if (ruleType === "fixed_date") {
      if (month < 1 || month > 12) return "Month must be 1-12";
      if (day < 1 || day > 31)     return "Day must be 1-31";
    } else if (ruleType === "floating_dow") {
      if (month < 1 || month > 12)          return "Month must be 1-12";
      if (![-1,1,2,3,4,5].includes(week))   return "Week must be 1-5 or -1 (last)";
      if (dow < 0 || dow > 6)               return "Day of week must be 0-6";
    } else if (ruleType === "easter_offset") {
      if (!Number.isInteger(Number(offsetDays))) return "Offset days must be an integer";
    } else if (ruleType === "custom") {
      if (ruleConfig === null) return "Custom rule_config must be valid JSON";
    }

    return null;
  };

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }

    setSaving(true); setError("");
    try {
      const payload = {
        name: name.trim(),
        holiday_type: holidayType,
        rule_type:    ruleType,
        rule_config:  ruleConfig,
        display_order: Number(displayOrder),
        is_active: isActive,
      };

      if (isEdit) {
        await updateHoliday(initialData.id, payload);
      } else {
        await createHoliday(payload);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 500 }}>
        {isEdit ? "Edit Holiday" : "New Holiday"}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <TextField
          label="Name" required size="small" fullWidth autoFocus={!isEdit}
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={saving}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.6fr", gap: 2, mb: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={holidayType}
              onChange={e => setHolidayType(e.target.value)}
              disabled={saving}
            >
              {HOLIDAY_TYPES.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: 13, textTransform: "capitalize" }}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Rule type</InputLabel>
            <Select
              label="Rule type"
              value={ruleType}
              onChange={e => setRuleType(e.target.value)}
              disabled={saving}
            >
              {RULE_TYPES.map(r => (
                <MenuItem key={r.value} value={r.value} sx={{ fontSize: 13 }}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Order" type="number" size="small" fullWidth
            value={displayOrder}
            onChange={e => setDisplayOrder(Number(e.target.value))}
            disabled={saving}
          />
        </Box>

        {/* ── Rule-config sub-fields (switch based on rule_type) ── */}
        <Box sx={{
          p: 2, mb: 2,
          background: "var(--mui-palette-background-default, #f7f7f7)",
          borderRadius: 2,
        }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary", mb: 1.5,
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Rule config
          </Typography>

          {ruleType === "fixed_date" && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Month</InputLabel>
                <Select label="Month" value={month} onChange={e => setMonth(Number(e.target.value))} disabled={saving}>
                  {MONTHS.map((m, i) => (
                    <MenuItem key={i} value={i + 1} sx={{ fontSize: 13 }}>
                      {i + 1} — {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Day" type="number" size="small" fullWidth
                inputProps={{ min: 1, max: 31 }}
                value={day}
                onChange={e => setDay(Number(e.target.value))}
                disabled={saving}
              />
            </Box>
          )}

          {ruleType === "floating_dow" && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Month</InputLabel>
                <Select label="Month" value={month} onChange={e => setMonth(Number(e.target.value))} disabled={saving}>
                  {MONTHS.map((m, i) => (
                    <MenuItem key={i} value={i + 1} sx={{ fontSize: 13 }}>{i + 1} — {m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Week</InputLabel>
                <Select label="Week" value={week} onChange={e => setWeek(Number(e.target.value))} disabled={saving}>
                  {WEEKS.map(w => (
                    <MenuItem key={w.value} value={w.value} sx={{ fontSize: 13 }}>{w.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Day of week</InputLabel>
                <Select label="Day of week" value={dow} onChange={e => setDow(Number(e.target.value))} disabled={saving}>
                  {DOWS.map((d, i) => (
                    <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{i} — {d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {ruleType === "easter_offset" && (
            <TextField
              label="Offset days (+ or -)" type="number" size="small" fullWidth
              value={offsetDays}
              onChange={e => setOffsetDays(Number(e.target.value))}
              disabled={saving}
              helperText="Good Friday = -2 (2 days before Easter)"
            />
          )}

          {ruleType === "custom" && (
            <TextField
              label="Raw rule_config JSON" size="small" fullWidth
              multiline minRows={4} maxRows={10}
              value={customJson}
              onChange={e => setCustomJson(e.target.value)}
              disabled={saving}
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
              helperText="Advanced - provide a valid JSON object directly"
            />
          )}

          {/* Live JSON preview */}
          <Box sx={{ mt: 1.5, p: 1, background: "#fff", borderRadius: 1,
            border: "0.5px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: 10, color: "text.disabled",
              textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
              JSON preview
            </Typography>
            <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: "text.secondary" }}>
              {ruleConfig === null ? "(invalid JSON)" : JSON.stringify(ruleConfig)}
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 2 }}>
          ⚠ After creating, updating, or deleting a holiday, click <b>Regenerate Calendar</b> on
          the Calendar tab to apply changes to upcoming dates.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none", borderRadius: "20px",
            background: BLUE, "&:hover": { background: "#2A6FB0" },
          }}
        >
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Create holiday")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}